import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import { isModernPlanningStore } from '../utils/planningStores';
import { isModernKpiStore } from '../utils/kpiModernAccess';

export default function Header({ currentPage, onPageChange, onAdminAction, unreadChat, currentStore }) {
  const { user, logout, isAdmin, canView } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const adminRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (adminRef.current && !adminRef.current.contains(e.target)) setAdminOpen(false);
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!user) return null;

  const initials = user.username.substring(0, 2).toUpperCase();
  const prototypeMode = currentPage === 'planning-test';
  const prototypeParent = 'planning';
  const modernPlanningMode = currentPage === 'planning' && isModernPlanningStore(currentStore);
  const modernKpiMode = currentPage === 'chiffres' && isModernKpiStore(currentStore);
  const isPhilippe = user.username.trim().toLowerCase() === 'philippe';
  const isTestStore = String(currentStore?.name || '').trim().replace(/\s+/g, ' ').toLowerCase() === 'magasin test mpl';
  const showAdminMenu = isAdmin || isPhilippe;
  const showOnMobile = (pageId) => {
    if (isAdmin) return true;
    if (pageId === 'planning' || pageId === 'messages') return true;
    return (user?.stores || []).some(store => store.mobile_full_access && store[`view_${pageId}`]);
  };

  const pages = [
    { id: 'planning', icon: '📋', label: 'Planning', show: canView('planning') },
    { id: 'chiffres', icon: '📊', label: 'Chiffres', show: canView('chiffres') },
    { id: 'dluo', icon: '📦', label: 'DLUO', show: canView('dluo') },
    { id: 'stocks', icon: '🏷️', label: 'Stocks', show: canView('stocks') },
    { id: 'messages', icon: '💬', label: 'Messages', show: true, badge: unreadChat },
    { id: 'planning-test', icon: '', label: 'TEST 3D', show: isPhilippe && isTestStore && (currentPage === 'planning' || currentPage === 'planning-test'), test: true },
  ];

  const visiblePages = pages.filter(p => p.show && (!prototypeMode || !p.test) && (!isMobile || showOnMobile(p.id)));

  const handleNav = (pageId) => {
    if (prototypeMode && pageId === prototypeParent) return;
    onPageChange(pageId);
  };

  return (
    <header className={`header ${prototypeMode || modernPlanningMode || modernKpiMode ? 'planning-test-header' : ''}`}>
      <div className="header-brand">
        <div className="header-logo"><img src="/logo.png" alt="GM VS" style={{ height: 32, borderRadius: 6 }} /></div>
        <div>
          <div className="header-title">Gestion Magasin</div>
          <div className="header-subtitle">Vapostore</div>
        </div>
      </div>
      
      {/* Desktop nav */}
      <nav className="header-nav">
        {visiblePages.map(p => (
          <button key={p.id} className={`header-nav-link ${p.test ? 'header-test-link' : ''} ${(currentPage === p.id || (prototypeMode && p.id === prototypeParent)) ? 'active' : ''}`}
            onClick={() => handleNav(p.id)} style={{ position: 'relative' }}>
            {p.icon} {p.label}
            {p.badge > 0 && <span className="header-chat-badge">{p.badge}</span>}
          </button>
        ))}
      </nav>
      
      <div className="header-right">
        {/* Mobile nav buttons */}
        <div className="header-mobile-nav">
          {visiblePages.map(p => (
            <button key={p.id} className={`btn btn-sm ${p.test ? 'header-test-link' : currentPage === p.id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handleNav(p.id)} style={{ position: 'relative', padding: '4px 6px', fontSize: '0.75rem' }}>
              {p.icon}
              {p.badge > 0 && <span className="header-chat-badge">{p.badge}</span>}
            </button>
          ))}
        </div>

        {/* Desktop only: theme + admin + user + logout */}
        <div className="header-desktop-only">
          <ThemeToggle />
          {showAdminMenu && (
            <div className="admin-menu" ref={adminRef} style={{ position: 'relative' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setAdminOpen(!adminOpen)}>⚙️ Admin</button>
              {adminOpen && (
                <div className="admin-menu-dropdown" style={{ right: 0, left: 'auto' }}>
                  {isAdmin && <button className="admin-menu-item" onClick={() => { onAdminAction?.('users'); setAdminOpen(false); }}>👥 Utilisateurs</button>}
                  {isAdmin && <button className="admin-menu-item" onClick={() => { onAdminAction?.('stores'); setAdminOpen(false); }}>🏪 Magasins</button>}
                  {isAdmin && <button className="admin-menu-item" onClick={() => { onAdminAction?.('figures'); setAdminOpen(false); }}>📊 Chiffres</button>}
                  {isAdmin && <div className="admin-menu-divider" />}
                  {isAdmin && <button className="admin-menu-item" onClick={() => { onAdminAction?.('sessions'); setAdminOpen(false); }}>🔌 Connexions</button>}
                  {isAdmin && <button className="admin-menu-item" onClick={() => { onAdminAction?.('tasks'); setAdminOpen(false); }}>📝 Tâches</button>}
                  <button className="admin-menu-item" onClick={() => { onAdminAction?.('news'); setAdminOpen(false); }}>📰 News</button>
                </div>
              )}
            </div>
          )}
          <div className="header-user">
            <div className="header-user-avatar" style={{ background: user.color || '#4f8fff' }}>{initials}</div>
            <div>
              <div className="header-user-name">{user.username}</div>
              <div className="header-user-role">{isAdmin ? '⚡ Admin' : user.role === 'responsable' ? '🔑 Responsable' : 'Utilisateur'}</div>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={logout}>Déconnexion</button>
        </div>

        {/* Mobile: hamburger menu */}
        <div className="header-mobile-menu" ref={menuRef}>
          <button className="header-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            <div className="header-user-avatar" style={{ background: user.color || '#4f8fff', width: 28, height: 28, fontSize: '0.6rem' }}>{initials}</div>
          </button>
          {menuOpen && (
            <div className="header-mobile-dropdown">
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="header-user-avatar" style={{ background: user.color || '#4f8fff', width: 28, height: 28, fontSize: '0.6rem' }}>{initials}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{user.username}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{isAdmin ? '⚡ Admin' : user.role === 'responsable' ? '🔑 Responsable' : '👤 Utilisateur'}</div>
                </div>
              </div>
              <div style={{ padding: '6px 0' }}>
                <div style={{ padding: '6px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Thème</span>
                  <ThemeToggle />
                </div>
                {showAdminMenu && (
                  <>
                    <div className="admin-menu-divider" />
                    {isAdmin && <button className="admin-menu-item" style={{ width: '100%', fontSize: '0.8rem' }} onClick={() => { onAdminAction?.('users'); setMenuOpen(false); }}>👥 Utilisateurs</button>}
                    {isAdmin && <button className="admin-menu-item" style={{ width: '100%', fontSize: '0.8rem' }} onClick={() => { onAdminAction?.('stores'); setMenuOpen(false); }}>🏪 Magasins</button>}
                    {isAdmin && <button className="admin-menu-item" style={{ width: '100%', fontSize: '0.8rem' }} onClick={() => { onAdminAction?.('figures'); setMenuOpen(false); }}>📊 Chiffres</button>}
                    {isAdmin && <button className="admin-menu-item" style={{ width: '100%', fontSize: '0.8rem' }} onClick={() => { onAdminAction?.('sessions'); setMenuOpen(false); }}>🔌 Connexions</button>}
                    {isAdmin && <button className="admin-menu-item" style={{ width: '100%', fontSize: '0.8rem' }} onClick={() => { onAdminAction?.('tasks'); setMenuOpen(false); }}>📝 Tâches</button>}
                    <button className="admin-menu-item" style={{ width: '100%', fontSize: '0.8rem' }} onClick={() => { onAdminAction?.('news'); setMenuOpen(false); }}>📰 News</button>
                  </>
                )}
                <div className="admin-menu-divider" />
                <button className="admin-menu-item" style={{ width: '100%', fontSize: '0.8rem', color: 'var(--accent-red)' }} onClick={() => { setMenuOpen(false); logout(); }}>🚪 Déconnexion</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
